<template>
  <!-- A custom landing page replaces the whole built-in page, not part of it:
       an uploaded document is a complete marketing page, and framing it inside
       the shell's own hero would fight it for the same screen. -->
  <CustomLanding
    v-if="customLandingUrl"
    :html-url="customLandingUrl"
    :app-name="tenantName"
    :primary="primaryColour"
    :loading="loading"
    :error-message="errorMessage ?? ''"
    @signin="signIn"
  />

  <div v-else class="login-page">
    <div class="login-page__glow" aria-hidden="true"></div>

    <!-- Product hero — logo, name, and (when App Home is configured) the
         welcome copy plus a non-clickable "what's inside" hint row. Degrades
         to just logo + name when the tenant has no homePage set. -->
    <section class="login-page__hero">
      <LogoTile :branding="branding" :alt="tenantName" :size="56" />
      <h1 class="login-hero__name">{{ tenantName }}</h1>
      <p v-if="welcomeTitle" class="login-hero__welcome-title">{{ welcomeTitle }}</p>
      <p v-if="welcomeText" class="login-hero__welcome-text">{{ welcomeText }}</p>

      <div v-if="hintTiles.length" class="login-hero__hints">
        <div v-for="tile in hintTiles" :key="tile.route" class="login-hero__hint">
          <v-icon size="16" color="primary">{{ tile.icon }}</v-icon>
          <span>{{ tile.title }}</span>
        </div>
      </div>
    </section>

    <section class="login-page__panel">
      <div class="login-signin">
        <h2 class="login-signin__title">Sign in</h2>
        <p v-if="errorMessage" class="login-signin__error">{{ errorMessage }}</p>
        <v-btn color="primary" size="large" block :loading="loading" @click="signIn">Sign in</v-btn>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useSessionStore } from "@/stores/session";
import LogoTile from "@/shell/LogoTile.vue";
import CustomLanding from "@/shell/CustomLanding.vue";
import type { TenantHomeTile } from "@/tenant/types";

/** Matches `applyBranding()`'s own guard — the colour is handed to the landing
 *  document as a query param, so it must never be free text. */
const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;
const DEFAULT_PRIMARY = "#649ECE";

/** "What's inside" hint row shows at most this many tiles — a preview, not a full nav. */
const MAX_HINT_TILES = 4;

export default defineComponent({
  name: "Login",
  components: { LogoTile, CustomLanding },
  setup() {
    const session = useSessionStore();
    const loading = ref(false);
    const errorMessage = computed(() => session.errorMessage);

    const tenantName = computed(() => session.tenant?.name || "MetaShip");
    const branding = computed(() => session.tenant?.branding ?? null);
    const homePage = computed(() => session.tenant?.homePage ?? null);

    // The backend degrades a custom page with no document back to "default",
    // so mode alone is enough — no separate null check on the URL.
    const loginPage = computed(() => session.tenant?.loginPage ?? null);
    const customLandingUrl = computed<string | null>(() =>
      loginPage.value?.mode === "custom" ? loginPage.value.htmlUrl : null,
    );
    const primaryColour = computed<string>(() => {
      const colour = branding.value?.colours?.primary;
      return colour && HEX_COLOUR.test(colour) ? colour : DEFAULT_PRIMARY;
    });

    // Only shown when it says something the app name doesn't already say.
    const welcomeTitle = computed(() => {
      const title = homePage.value?.welcomeTitle?.trim();
      if (!title) return null;
      return title.toLowerCase() === tenantName.value.trim().toLowerCase() ? null : title;
    });
    const welcomeText = computed(() => homePage.value?.welcomeText || null);

    // Pre-auth, so there's no resolved-modules/allowedItems permission model
    // to check yet (that's post-sign-in, see AppHome.vue) — this is purely a
    // marketing preview of what the tenant configured, names only, not a nav.
    const hintTiles = computed<TenantHomeTile[]>(() =>
      homePage.value?.enabled ? (homePage.value.tiles ?? []).slice(0, MAX_HINT_TILES) : [],
    );

    async function signIn(): Promise<void> {
      loading.value = true;
      try {
        await session.beginLogin();
      } finally {
        loading.value = false;
      }
    }

    return {
      tenantName,
      branding,
      welcomeTitle,
      welcomeText,
      hintTiles,
      loading,
      errorMessage,
      signIn,
      customLandingUrl,
      primaryColour,
    };
  },
});
</script>

<style scoped>
.login-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  overflow: hidden;
  background: var(--tenant-background, #111927);
}

/* Subtle radial glow of the tenant's primary colour — `--tenant-primary` is
   already guarded to a strict #rrggbb (or the MetaShip default) by
   `applyBranding()` at boot, so no re-validation is needed here. A blurred,
   low-opacity circle rather than a gradient function keeps this readable
   across every supported browser without leaning on `color-mix()`. */
.login-page__glow {
  position: absolute;
  top: -15%;
  left: -8%;
  width: 55vw;
  height: 55vw;
  max-width: 620px;
  max-height: 620px;
  border-radius: 50%;
  background: var(--tenant-primary, #649ece);
  opacity: 0.18;
  filter: blur(120px);
  pointer-events: none;
}

.login-page__hero {
  position: relative;
  z-index: 1;
  flex: 1 1 55%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 56px 64px;
}
.login-hero__name {
  font-size: 28px;
  font-weight: 700;
  margin: 20px 0 0;
}
.login-hero__welcome-title {
  font-size: 15px;
  font-weight: 600;
  opacity: 0.85;
  margin: 8px 0 0;
}
.login-hero__welcome-text {
  font-size: 13px;
  line-height: 1.5;
  opacity: 0.7;
  margin: 8px 0 0;
  max-width: 440px;
}

.login-hero__hints {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;
}
.login-hero__hint {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 20px;
  border: 1px solid rgba(var(--v-theme-on-background), 0.12);
  font-size: 12px;
  opacity: 0.8;
}

.login-page__panel {
  position: relative;
  z-index: 1;
  flex: 1 1 45%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.login-signin {
  width: 100%;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 32px;
  border-radius: 12px;
  background: rgb(var(--v-theme-foreground));
  border: 1px solid rgba(var(--v-theme-on-background), 0.08);
}
.login-signin__title {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}
.login-signin__error {
  color: rgb(var(--v-theme-error));
  font-size: 13px;
  margin: -6px 0 0;
}

@media (max-width: 720px) {
  .login-page {
    flex-direction: column;
  }
  .login-page__hero {
    padding: 40px 24px 8px;
    align-items: center;
    text-align: center;
  }
  .login-hero__welcome-text {
    max-width: none;
  }
  .login-hero__hints {
    justify-content: center;
  }
  .login-page__panel {
    padding: 24px;
  }
}
</style>
